export class AudioManager {
    private context: AudioContext;

    private soundGain: GainNode;
    private musicGain: GainNode;

    private soundBuffers: Map<string, AudioBuffer> = new Map();

    private musicBuffer: AudioBuffer | null = null;

    private musicSource: AudioBufferSourceNode | null = null;

    private soundEnabled = false;
    private musicEnabled = false;

    constructor() {
        this.context = new AudioContext();

        this.soundGain = this.context.createGain();
        this.soundGain.connect(this.context.destination);

        this.musicGain = this.context.createGain();
        this.musicGain.connect(this.context.destination);
    }

    async loadSound(name: string, url: string): Promise<void> {
        const buffer = await this.fetchAndDecode(url);
        this.soundBuffers.set(name, buffer);
    }

    async loadMusic(url: string): Promise<void> {
        const buffer = await this.fetchAndDecode(url);
        this.musicBuffer = buffer;

        if (this.musicEnabled) {
            this.stopMusicSource();
            this.startMusicSource();
        }
    }

    playSound(name: string): void {
        if (!this.soundEnabled) return;

        const buffer = this.soundBuffers.get(name);
        if (!buffer) {
            console.warn(`AudioManager: sound "${name}" is not loaded.`);
            return;
        }

        this.resumeIfSuspended();

        const source = this.context.createBufferSource();
        source.buffer = buffer;
        source.connect(this.soundGain);
        source.start(0);
    }

    setSoundVolume(volume: number): void {
        this.soundGain.gain.value = clamp01(volume);
    }

    setMusicVolume(volume: number): void {
        this.musicGain.gain.value = clamp01(volume);
    }

    getSoundVolume(): number {
        return this.soundGain.gain.value;
    }

    getMusicVolume(): number {
        return this.musicGain.gain.value;
    }

    setSoundEnabled(enabled: boolean): void {
        this.soundEnabled = enabled;
    }

    setMusicEnabled(enabled: boolean): void {
        if (enabled === this.musicEnabled) return;
        this.musicEnabled = enabled;

        if (enabled) {
            this.resumeIfSuspended();
            this.startMusicSource();
        } else {
            this.stopMusicSource();
        }
    }

    toggleMusic(): void {
        this.setMusicEnabled(!this.musicEnabled);
    }

    isMusicEnabled(): boolean {
        return this.musicEnabled;
    }

    dispose(): void {
        this.stopMusicSource();
        void this.context.close();
    }

    private async fetchAndDecode(url: string): Promise<AudioBuffer> {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        return this.context.decodeAudioData(arrayBuffer);
    }

    private startMusicSource(): void {
        if (!this.musicBuffer) {
            console.warn('AudioManager: no music loaded yet.');
            return;
        }

        const source = this.context.createBufferSource();
        source.buffer = this.musicBuffer;
        source.loop = true;
        source.connect(this.musicGain);
        source.start(0);

        this.musicSource = source;
    }

    private stopMusicSource(): void {
        if (this.musicSource) {
            this.musicSource.stop();
            this.musicSource.disconnect();
            this.musicSource = null;
        }
    }

    private resumeIfSuspended(): void {
        if (this.context.state === 'suspended') {
            void this.context.resume();
        }
    }
}

function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}